import { addDays, addMonths, getDate, isAfter, lastDayOfMonth, parseISO, set, startOfDay } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import {
  ApiEnrichScheduleFields,
  ApiRefreshScheduleFields,
  ScheduleConfig,
  ScheduleDraft,
  ScheduleFrequency,
  ScheduleTimezone,
  ScheduleValue,
  SourcingList,
  SourcingSavedSearch,
  Weekday,
} from '../../../schemas/SourcingList.schema';

// ────────────────────────── Constants ──────────────────────────

export const WEEKDAYS: ReadonlyArray<{ value: Weekday; label: string }> = [
  { value: 'SUNDAY', label: 'Sun' },
  { value: 'MONDAY', label: 'Mon' },
  { value: 'TUESDAY', label: 'Tue' },
  { value: 'WEDNESDAY', label: 'Wed' },
  { value: 'THURSDAY', label: 'Thu' },
  { value: 'FRIDAY', label: 'Fri' },
  { value: 'SATURDAY', label: 'Sat' },
];

const WEEKDAY_BY_INDEX: ReadonlyArray<Weekday> = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

export const FREQUENCIES: ReadonlyArray<{ value: ScheduleFrequency; label: string }> = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

const WEEKDAYS_MON_TO_FRI: ReadonlyArray<Weekday> = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

const IANA_TIMEZONE: Record<ScheduleTimezone, string> = {
  ET: 'America/New_York',
  CT: 'America/Chicago',
  MT: 'America/Denver',
  PT: 'America/Los_Angeles',
};

export const TIMEZONES: ReadonlyArray<{ value: ScheduleTimezone; label: string }> = [
  { value: 'ET', label: 'Eastern Time (ET)' },
  { value: 'CT', label: 'Central Time (CT)' },
  { value: 'MT', label: 'Mountain Time (MT)' },
  { value: 'PT', label: 'Pacific Time (PT)' },
];

export const DEFAULT_TIMEZONE: ScheduleTimezone = 'ET';

export function dateToWeekday(date: Date, tz: ScheduleTimezone): Weekday {
  const zoned = toZonedTime(date, IANA_TIMEZONE[tz]);
  return WEEKDAY_BY_INDEX[zoned.getDay()];
}

export type ScheduleSubject = 'query' | 'enrichment';

// ────────────────────────── Time helpers ──────────────────────────

export interface TimeParts {
  hour12: number;
  ampm: 'AM' | 'PM';
}

export function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function toHours24(hour12: number, ampm: 'AM' | 'PM'): number {
  if (ampm === 'AM') {
    return hour12 === 12 ? 0 : hour12;
  }
  return hour12 === 12 ? 12 : hour12 + 12;
}

function toHours12(hour24: number): TimeParts {
  if (hour24 === 0) {
    return { hour12: 12, ampm: 'AM' };
  }
  if (hour24 < 12) {
    return { hour12: hour24, ampm: 'AM' };
  }
  if (hour24 === 12) {
    return { hour12: 12, ampm: 'PM' };
  }
  return { hour12: hour24 - 12, ampm: 'PM' };
}

/** Read the wall-clock hour-of-day of `iso` in `tz`. */
export function parseTime(iso: string, tz: ScheduleTimezone): TimeParts {
  const zoned = toZonedTime(parseISO(iso), IANA_TIMEZONE[tz]);
  return toHours12(zoned.getHours());
}

/**
 * Build a `startAtUtc` for the given 12-hour time in `tz`, anchored on today.
 * Only the hour-of-day is meaningful for scheduling — the date is just a
 * container so the UTC offset reflects the current DST state.
 */
export function buildStartAtUtc(hour12: number, ampm: 'AM' | 'PM', tz: ScheduleTimezone): string {
  const todayInTz = startOfDay(toZonedTime(new Date(), IANA_TIMEZONE[tz]));
  const wall = set(todayInTz, { hours: toHours24(hour12, ampm) });
  return fromZonedTime(wall, IANA_TIMEZONE[tz]).toISOString();
}

/** "9AM" — wall-clock hour of `iso` rendered in `tz`. */
export function formatTime(iso: string, tz: ScheduleTimezone): string {
  return formatInTimeZone(parseISO(iso), IANA_TIMEZONE[tz], 'ha');
}

// ────────────────────────── Model (state transitions) ──────────────────────────

export function defaultSchedule(): ScheduleConfig {
  return {
    frequency: 'DAILY',
    startAtUtc: buildStartAtUtc(9, 'AM', DEFAULT_TIMEZONE),
    timezone: DEFAULT_TIMEZONE,
    daysOfWeek: [...WEEKDAYS_MON_TO_FRI],
    dayOfMonth: null,
  };
}

/**
 * Initial state for a brand-new "Run on a Schedule" mode where the user has
 * not chosen a frequency yet. Distinct from `defaultSchedule()` so the dialog
 * can render an empty state instead of pre-selecting DAILY + Mon–Fri.
 */
export function emptyDraft(): ScheduleDraft {
  return { frequency: null, startAtUtc: null, timezone: null, daysOfWeek: null, dayOfMonth: null };
}

/**
 * Promote a `ScheduleDraft` to a `ScheduleConfig` when the user picks the
 * first frequency. Leaves day-shape fields empty (DAILY/WEEKLY → `[]`,
 * MONTHLY → `null`) so `isScheduleValid` keeps the dialog disabled until the
 * user fills them in. Preserves any partially-set time from the draft.
 */
export function startSchedule(frequency: ScheduleFrequency, draft: ScheduleDraft): ScheduleConfig {
  const timezone = draft.timezone ?? DEFAULT_TIMEZONE;
  const startAtUtc = draft.startAtUtc ?? buildStartAtUtc(9, 'AM', timezone);
  if (frequency === 'MONTHLY') {
    return { frequency, startAtUtc, timezone, daysOfWeek: null, dayOfMonth: draft.dayOfMonth };
  }
  return { frequency, startAtUtc, timezone, daysOfWeek: draft.daysOfWeek ?? [], dayOfMonth: null };
}

/**
 * Rewrites the day-shape fields when the frequency changes so the new state is always
 * coherent for the chosen frequency (daysOfWeek only for DAILY/WEEKLY, dayOfMonth only
 * for MONTHLY). Keeps as much of the prior selection as each frequency allows.
 */
export function transitionFrequency(current: ScheduleConfig, next: ScheduleFrequency): ScheduleConfig {
  if (next === 'MONTHLY') {
    return { ...current, frequency: next, daysOfWeek: null, dayOfMonth: current.dayOfMonth ?? 1 };
  }
  if (next === 'WEEKLY') {
    const first = current.daysOfWeek?.[0];
    return { ...current, frequency: next, daysOfWeek: [first ?? 'MONDAY'], dayOfMonth: null };
  }
  return {
    ...current,
    frequency: next,
    daysOfWeek:
      current.daysOfWeek && current.daysOfWeek.length > 0 ? current.daysOfWeek : [...WEEKDAYS_MON_TO_FRI],
    dayOfMonth: null,
  };
}

/**
 * DAILY: multi-select — toggle `day` in/out. WEEKLY: single-select — `day` replaces the
 * existing selection. MONTHLY: no-op (daysOfWeek not used for that frequency).
 *
 * Sorts by canonical weekday order (Sunday = 0 .. Saturday = 6), matching the chip
 * row in the UI, regardless of the order the user clicked.
 */
export function toggleWeekday(current: ScheduleConfig, day: Weekday): ScheduleConfig {
  if (current.frequency === 'MONTHLY') {
    return current;
  }
  if (current.frequency === 'WEEKLY') {
    return { ...current, daysOfWeek: [day] };
  }
  const set = new Set(current.daysOfWeek ?? []);
  if (set.has(day)) {
    set.delete(day);
  } else {
    set.add(day);
  }
  const order = new Map(WEEKDAYS.map((w, i) => [w.value, i] as const));
  const sorted = [...set].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  return { ...current, daysOfWeek: sorted };
}

// ────────────────────────── Validation ──────────────────────────

/**
 * `null` (Run Manually) is always valid. For a scheduled config, DAILY/WEEKLY require at
 * least one weekday selected, and MONTHLY requires a dayOfMonth — these mirror the
 * "missing field" branches in `formatSummary` and gate dialog submission.
 */
export function isScheduleValid(schedule: ScheduleValue): boolean {
  if (schedule === null) {
    return true;
  }
  if (schedule.frequency === null) {
    return false;
  }
  if (schedule.frequency === 'MONTHLY') {
    return schedule.dayOfMonth !== null;
  }
  return (schedule.daysOfWeek ?? []).length > 0;
}

// ────────────────────────── Presentation (info-bar summary) ──────────────────────────

export function describeWhen({ frequency, dayOfMonth, daysOfWeek }: ScheduleConfig): string | null {
  if (frequency === 'MONTHLY') {
    return dayOfMonth ? `on day ${dayOfMonth} of every month` : null;
  }
  const days = daysOfWeek ?? [];
  if (days.length === 0) {
    return null;
  }
  const order = new Map(WEEKDAYS.map((w, i) => [w.value, i] as const));
  const sorted = [...days].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  const labels = sorted.map((d) => WEEKDAYS.find((w) => w.value === d)?.label).filter(Boolean);
  return `every ${labels.join(', ')}`;
}

export function formatSummary(schedule: ScheduleConfig | ScheduleDraft, subject: ScheduleSubject): string {
  const capitalized = subject === 'query' ? 'Query' : 'Enrichment';
  if (schedule.frequency === null) {
    return `${capitalized} will not be run until a frequency is selected.`;
  }
  const when = describeWhen(schedule);
  if (when === null) {
    const missing = schedule.frequency === 'MONTHLY' ? 'a day of the month' : 'at least one day';
    return `${capitalized} will not be run until ${missing} is selected.`;
  }
  return `${capitalized} will be run ${when} at ${formatTime(schedule.startAtUtc, schedule.timezone)} ${schedule.timezone}.`;
}

export function formatManualSummary(subject: ScheduleSubject): string {
  const capitalized = subject === 'query' ? 'Query' : 'Enrichment';
  return `${capitalized} will not be run automatically. You can run it manually at any time.`;
}

export function formatNextRun(next: Date): string {
  return next.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// ────────────────────────── Next-run computation ──────────────────────────

/**
 * Compute the next scheduled fire time for `schedule`, strictly after `now`.
 *
 * DAILY/WEEKLY: scan up to 7 days forward looking for a day-of-week in
 * `daysOfWeek` whose target wall-clock time (in the schedule's tz) is after
 * `now`.
 *
 * MONTHLY: this month at `dayOfMonth` (clamped to the month's last day —
 * Feb + dayOfMonth=31 becomes Feb 28/29) if still future, else next month
 * with the same clamping.
 *
 * Returns `null` when `schedule` is incomplete (no daysOfWeek for
 * DAILY/WEEKLY, no dayOfMonth for MONTHLY) — mirrors `isScheduleValid`.
 */
export function calculateNextRun(schedule: ScheduleConfig, now: Date = new Date()): Date | null {
  const tz = IANA_TIMEZONE[schedule.timezone];
  const startInTz = toZonedTime(parseISO(schedule.startAtUtc), tz);
  const targetHour = startInTz.getHours();
  const nowInTz = toZonedTime(now, tz);

  // Build the UTC instant corresponding to `base`'s calendar date at `targetHour` in tz.
  const buildCandidate = (base: Date): Date => {
    const wall = set(base, { hours: targetHour, minutes: 0, seconds: 0, milliseconds: 0 });
    return fromZonedTime(wall, tz);
  };

  if (schedule.frequency === 'MONTHLY') {
    if (schedule.dayOfMonth === null) {
      return null;
    }
    const clampToMonth = (base: Date): Date => {
      const lastDay = getDate(lastDayOfMonth(base));
      return set(base, { date: Math.min(schedule.dayOfMonth as number, lastDay) });
    };
    const thisCandidate = buildCandidate(clampToMonth(nowInTz));
    if (isAfter(thisCandidate, now)) {
      return thisCandidate;
    }
    return buildCandidate(clampToMonth(addMonths(nowInTz, 1)));
  }

  const daysOfWeek = schedule.daysOfWeek ?? [];
  if (daysOfWeek.length === 0) {
    return null;
  }
  // 8 iterations (not 7) so today + time-already-passed rolls to the same
  // weekday next week.
  for (let i = 0; i < 8; i++) {
    const dayInTz = addDays(nowInTz, i);
    const candidate = buildCandidate(dayInTz);
    if (daysOfWeek.includes(WEEKDAY_BY_INDEX[dayInTz.getDay()]) && isAfter(candidate, now)) {
      return candidate;
    }
  }
  return null;
}

// ────────────────────────── API serialization ──────────────────────────

/**
 * Project a ScheduleValue onto one contract slice. Null (Run Manually) emits nulls for the
 * date/day fields — matching what the backend persists when the user disables the schedule.
 */
export function buildSchedulePayload(
  slice: 'refresh' | 'enrich',
  schedule: ScheduleValue
): ApiRefreshScheduleFields | ApiEnrichScheduleFields {
  if (slice === 'refresh') {
    return {
      refreshScheduleFrequency: schedule?.frequency ?? null,
      refreshScheduleStartAtUtc: schedule?.startAtUtc ?? null,
      refreshScheduleTimezone: schedule?.timezone ?? null,
      refreshScheduleDaysOfWeek: schedule?.daysOfWeek ?? null,
      refreshScheduleDayOfMonth: schedule?.dayOfMonth ?? null,
    };
  }
  return {
    enrichScheduleFrequency: schedule?.frequency ?? null,
    enrichScheduleStartAtUtc: schedule?.startAtUtc ?? null,
    enrichScheduleTimezone: schedule?.timezone ?? null,
    enrichScheduleDaysOfWeek: schedule?.daysOfWeek ?? null,
    enrichScheduleDayOfMonth: schedule?.dayOfMonth ?? null,
  };
}

/**
 * Inverse of buildSchedulePayload: reads the slice that matters for this list type
 * (refresh for saved searches, enrich for static lists) and returns a ScheduleValue
 * for hydrating the dialog. Null when the persisted slice has no frequency set.
 */
export function hydrateSchedule(list: SourcingList | SourcingSavedSearch): ScheduleConfig | null {
  const slice =
    list.listType === 'active'
      ? {
          frequency: list.refreshScheduleFrequency,
          startAtUtc: list.refreshScheduleStartAtUtc,
          timezone: list.refreshScheduleTimezone,
          daysOfWeek: list.refreshScheduleDaysOfWeek,
          dayOfMonth: list.refreshScheduleDayOfMonth,
        }
      : {
          frequency: list.enrichScheduleFrequency,
          startAtUtc: list.enrichScheduleStartAtUtc,
          timezone: list.enrichScheduleTimezone,
          daysOfWeek: list.enrichScheduleDaysOfWeek,
          dayOfMonth: list.enrichScheduleDayOfMonth,
        };
  if (!slice.frequency) {
    return null;
  }
  const timezone = slice.timezone ?? DEFAULT_TIMEZONE;
  return {
    frequency: slice.frequency,
    startAtUtc: slice.startAtUtc ?? buildStartAtUtc(9, 'AM', timezone),
    timezone,
    daysOfWeek: slice.daysOfWeek ?? null,
    dayOfMonth: slice.dayOfMonth ?? null,
  };
}
